/** @jsxImportSource theme-ui */
import { Box, Checkbox, Flex, Label, Text } from 'theme-ui';
import { TrashIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useDeleteCustomerMutation } from '@/store/apiService';
import { SuperAdminCustomerMatch } from '@/types/types';

const ActionsCell = ({ row }: { row: { original: SuperAdminCustomerMatch } }) => {
    const customer = row.original;
    const isProfileOnly = Boolean(customer.IsProfileOnly);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteProfileAndLogin, setDeleteProfileAndLogin] = useState(true);
    const [deleteCustomer, deleteState] = useDeleteCustomerMutation();

    const name = [customer.Firstname, customer.Lastname].filter(Boolean).join(' ').trim();
    const label = customer.Email || name || (isProfileOnly ? 'this user profile' : 'this customer');

    const content = isProfileOnly ? (
        `This permanently deletes the user profile and login (Keycloak) for ${label}. This person has no customer registration in any company. Are you sure?`
    ) : (
        <Box>
            <Text>
                {`This permanently deletes ${label} at ${
                    customer.CompanyName ?? customer.CompanyId
                } and anonymizes their bookings. Customers with future active bookings cannot be deleted.`}
            </Text>
            <Label sx={{ alignItems: 'center', marginTop: 3, fontSize: 1 }}>
                <Checkbox
                    checked={deleteProfileAndLogin}
                    onChange={(e) => setDeleteProfileAndLogin(e.target.checked)}
                />
                Also delete their user profile and login (Keycloak) if this is their last customer
                registration in any company
            </Label>
        </Box>
    );

    return (
        <Flex sx={{ justifyContent: 'flex-end', flexGrow: 1, alignItems: 'center', gap: 2 }}>
            {confirmOpen ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={deleteState.isLoading}
                    headingText={isProfileOnly ? 'Delete user profile' : 'Delete customer'}
                    content={content}
                    onNo={() => setConfirmOpen(false)}
                    onYes={() => {
                        deleteCustomer({
                            Id: customer.CustomerId,
                            CompanyId: customer.CompanyId,
                            ...(isProfileOnly
                                ? {}
                                : { DeleteUserProfileAndIdentity: deleteProfileAndLogin }),
                        })
                            .unwrap()
                            .then(() => {
                                setConfirmOpen(false);
                                toast(isProfileOnly ? 'User profile deleted' : 'Customer deleted', {
                                    hideProgressBar: true,
                                });
                            })
                            .catch((err) => {
                                setConfirmOpen(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                        err?.data ??
                                        err?.message ??
                                        'Delete failed',
                                    { autoClose: 5000 }
                                );
                            });
                    }}
                />
            ) : null}
            <Button
                variant="basicDanger"
                isLoading={deleteState.isLoading}
                onClick={() => {
                    setDeleteProfileAndLogin(true);
                    setConfirmOpen(true);
                }}
                title={isProfileOnly ? 'Delete user profile' : 'Delete customer'}
            >
                <TrashIcon width={16} height={16} />
            </Button>
        </Flex>
    );
};

const CompanyCell = ({ row }: { row: { original: SuperAdminCustomerMatch } }) =>
    row.original.IsProfileOnly ? (
        <Text sx={{ fontStyle: 'italic', color: 'gray.7' }}>Profile only — no customer registration</Text>
    ) : (
        <>{row.original.CompanyName ?? ''}</>
    );

export const customerColumns = [
    {
        columnKey: 'CompanyName',
        Header: 'Company',
        accessor: 'CompanyName',
        width: '20%',
        Cell: CompanyCell,
    },
    {
        columnKey: 'Firstname',
        Header: 'First name',
        accessor: 'Firstname',
    },
    {
        columnKey: 'Lastname',
        Header: 'Last name',
        accessor: 'Lastname',
    },
    {
        columnKey: 'Email',
        Header: 'Email',
        accessor: 'Email',
        width: '20%',
    },
    {
        columnKey: 'Phone',
        Header: 'Phone',
        accessor: 'Phone',
    },
    {
        columnKey: 'ActiveBookingsCount',
        Header: 'Active bookings',
        accessor: 'ActiveBookingsCount',
        width: '110px',
    },
    {
        columnKey: 'TotalBookingsCount',
        Header: 'Total bookings',
        accessor: 'TotalBookingsCount',
        width: '110px',
    },
    {
        columnKey: 'Actions',
        accessor: 'Actions',
        Header: '',
        Cell: ActionsCell,
    },
];
